import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrayOf, func, node, number, shape, string } from 'prop-types';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';

// Components
import { Box, Button, CircularProgress, Divider, LinearProgress, Typography } from '@mui/material';

import { DrawerLayoutWrapper } from 'common';

const CreateWizard = ({ confirmCloseDialog, title, onClose, steps }) => {
  const dispatch = useDispatch();
  const initialRender = useRef(true);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);

  const initialLoadingData = useSelector(steps[currentStepIdx].loading);
  const loadingData = useSelector(steps[currentStepIdx].showSpinner);
  const disabled = useSelector(steps[currentStepIdx].disabled);
  const stepLabel = useSelector(steps[currentStepIdx].stepLabel);
  const totalSteps = steps.length - 1;
  const stepId = steps?.[currentStepIdx]?.id;
  const hasChanges = useSelector(steps[currentStepIdx].hasChanges);

  const progress = stepId ? (stepId / steps.length) * 100 : 0;

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    !loadingData && currentStepIdx < totalSteps && setCurrentStepIdx(currentStepIdx + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingData]);

  const handleRightButtonClick = () =>
    currentStepIdx < totalSteps
      ? steps[currentStepIdx].goNext(dispatch, setCurrentStepIdx, currentStepIdx)
      : onClose();

  const handleWizardClose = () => {
    if (hasChanges) {
      dispatch(
        openConfirmationDialog({
          cancelAction: () => {},
          confirmAction: () => {
            if (confirmCloseDialog) {
              confirmCloseDialog();
              return;
            }

            onClose();
          },
          header: 'Unsaved Changes',
          leftButtonActionType: 'confirm',
          leftButtonText: 'Continue',
          message:
            'Are you sure you want to leave this page? You have unsaved Carrier Authorizations',
          params: {},
          rightButtonActionType: 'cancel',
          rightButtonText: 'Keep Editing',
        })
      );
      return;
    }

    onClose();
  };

  const handleLeftButtonClick = () => {
    if (!currentStepIdx) {
      handleWizardClose();
      return;
    }

    if (steps[currentStepIdx].goBack(dispatch, setCurrentStepIdx)) return;
    setCurrentStepIdx(currentStepIdx - 1);
  };

  const buttons = (
    <Box alignItems="center" display="flex" gap={2} justifyContent="flex-end" pr={1} width="100%">
      {!!steps[currentStepIdx].leftLabel && (
        <Button disabled={loadingData} onClick={handleLeftButtonClick} variant="outlined">
          {loadingData && (
            <CircularProgress
              size={14}
              sx={{
                bottom: 0,
                color: 'secondary.main',
                left: 0,
                margin: 'auto',
                pointerEvents: 'none',
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            />
          )}
          {steps[currentStepIdx].leftLabel}
        </Button>
      )}
      {!!steps[currentStepIdx].rightLabel && (
        <Button disabled={disabled || loadingData} onClick={handleRightButtonClick}>
          {loadingData && (
            <CircularProgress
              size={14}
              sx={{
                bottom: 0,
                color: 'secondary.main',
                left: 0,
                margin: 'auto',
                pointerEvents: 'none',
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            />
          )}
          {steps[currentStepIdx].rightLabel}
        </Button>
      )}
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      isLoading={initialLoadingData}
      onHeaderButtonClick={handleWizardClose}
      sx={{
        '.DrawerLayoutWrapper-inner': {
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pb: 0,
        },
      }}
      title={title}
    >
      <>
        <Box display="flex" flexDirection="column" justifyContent="center" p={3} w={1}>
          <LinearProgress
            data-variant="magellanProgressBar"
            value={progress}
            variant="determinate"
          />
          <Typography variant="caption">{`${stepLabel} (${stepId} of ${steps.length})`}</Typography>
        </Box>
        <Divider />
        {steps[currentStepIdx].component}
      </>
    </DrawerLayoutWrapper>
  );
};

export default CreateWizard;

CreateWizard.propTypes = {
  confirmCloseDialog: func,
  onClose: func.isRequired,
  steps: arrayOf(
    shape({
      component: node,
      disabled: func,
      goBack: func,
      goNext: func,
      id: number,
      leftLabel: string,
      rightLabel: string,
      showSpinner: func,
    })
  ).isRequired,
  title: string.isRequired,
};

CreateWizard.defaultProps = {
  confirmCloseDialog: null,
};
